import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Menu } from 'antd';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { useQuery, useMutation } from '@apollo/client';
import { Spin, Form, Input, Button, message } from 'antd';
import jwtDecode from 'jwt-decode';
import cookies from 'js-cookie';
import classNames from 'classnames';
import { trim } from 'lodash';
import { cookieNames } from '~constants';
import { menuList } from '~constants/login-security';
import {
  QUERY_LANDLORD_LOGIN_SECURITY,
  CONFIRM_PASSWORD,
  UPDATE_ACCOUNT_PASSWORD,
} from './queries';
import event from '~helpers/mixpanel';

import APIToken from './apiToken';

const uesStyles = makeStyles(
  theme => ({
    root: {
      display: 'flex',
      width: '100%',
      height: 'calc(100vh - 200px)',
      minHeight: '600px',
      paddingTop: 32,
      paddingBottom: 32,
      backgroundColor: theme.colors.gray1,
      borderRadius: theme.borderRadius.min,
    },
    menu: {
      width: 200,
      height: '100%',
      '& .ant-menu-item-selected': {
        boxShadow: 'inset -2px 0px 0px #38B2A6',
        fontWeight: 'bold',
        position: 'relative',
        width: 200,
      },
    },
    titleWarp: {
      display: 'flex',
      boxShadow: theme.divider.dividerBottom,
    },
    contentBox: {
      flex: 1,
      marginLeft: 32,
      position: 'relative',
    },
    title: {
      ...theme.typography.h3,
      marginRight: 12,
      marginBottom: 0,
      paddingBottom: 16,
    },
    desc: {
      ...theme.typography.body2,
      lineHeight: '32px',
      color: theme.colors.captionColor,
    },
    fieldsBox: {
      width: 520,
    },
    fieldCard: {
      marginTop: 32,
      paddingBottom: 24,
      boxShadow: theme.divider.dividerBottom,
    },
    fieldCardLabel: {
      ...theme.typography.h5,
    },
    tipsWrap: {
      ...theme.typography.body4,
      position: 'absolute',
      top: 32,
      width: 266,
    },
    openEmailText: {
      color: `${theme.colors.mainGreen6} !important`,
    },
    loading: {
      ...theme.flex.center,
      width: '100%',
      height: '100%',
    },
    tipBox: {
      ...theme.typography.body4,
      position: 'absolute',
      top: 32,
      left: '60%',
      width: 266,
    },
    fieldCardFlex: {
      ...theme.flex.mainSpaceBetween,
    },
    fieldBox: {
      boxShadow: 'none',
    },
    hintbtn: {
      color: theme.colors.mainGreen6,
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '22px',
      cursor: 'pointer',
    },
    loginSecurityForm: {
      marginTop: '-16px',
      '& .ant-form-item': {
        marginBottom: '16px',
      },
      '& .ant-form-item-label': {
        lineHeight: '22px',
        marginBottom: '6px',
      },
    },
    submitBtn: {
      fontWeight: 'bold',
      fontSize: '14px',
    },
  }),
  { name: 'LoginAndScurity' },
);

const ResetPasswordForm = ({ form, email, setShowSetPasswordForm, id }) => {
  const { getFieldDecorator, validateFields, setFields, getFieldValue } = form;

  const { t } = useTranslation();

  const password = trim(getFieldValue('currentPassword'));

  const newPassword = trim(getFieldValue('newPassword'));
  const [loading, setLoading] = useState(false);

  const [confirmPassword] = useMutation(CONFIRM_PASSWORD, {
    variables: {
      input: { email: email, password },
    },
    onCompleted: () => {
      resetPasswordFn();
    },
    onError: () => {
      setLoading(false);
      setFields({
        currentPassword: {
          value: getFieldValue('currentPassword'),
          errors: [new Error(t('setPassword_form_current_password_error'))],
        },
      });
    },
  });

  const [resetPasswordFn] = useMutation(UPDATE_ACCOUNT_PASSWORD, {
    variables: { input: { password: newPassword, id } },
    onCompleted: () => {
      setLoading(false);
      setShowSetPasswordForm(false);
      message.success(t('setPassword_form_confirm_success'));
    },
    onError: () => {
      setLoading(false);
      message.error(t('setPassword_form_confirm_failed'));
    },
  });

  const handleSubmit = () => {
    setLoading(true);
    confirmPassword();
  };
  const confirm = () => {
    validateFields((error, values) => {
      if (!error) {
        const { currentPassword, newPassword, confirmPassword } = values;
        if (confirmPassword !== newPassword) {
          setFields({
            confirmPassword: {
              value: getFieldValue('confirmPassword'),
              errors: [new Error(t('setPassword_form_confirm_password_error'))],
            },
          });
        } else {
          handleSubmit(email, currentPassword);
        }
      }
    });
  };
  const classes = uesStyles();

  return (
    <div className={classes.loginSecurityForm}>
      <Form>
        <Form.Item label={t('login_security_account.current_password_label')}>
          {getFieldDecorator('currentPassword', {
            rules: [
              {
                validator: (rule, value, callback) => {
                  if (!value || value.length === 0) {
                    callback(t('setPassword_form_not_empty'));
                  } else if (value.length < 8) {
                    callback(t('cms:cms.account.basic_setting.password.error.too_short'));
                  }
                  callback();
                },
              },
            ],
            validateTrigger: 'onBlur',
          })(<Input.Password size="large" />)}
        </Form.Item>
        <Form.Item label={t('login_security_account.new_password_label')}>
          {getFieldDecorator('newPassword', {
            rules: [
              {
                validator: (rule, value, callback) => {
                  if (!value || value.length === 0) {
                    callback(t('setPassword_form_not_empty'));
                  } else if (value.length < 8) {
                    callback(t('cms:cms.account.basic_setting.password.error.too_short'));
                  }

                  callback();
                },
              },
            ],
            validateTrigger: 'onBlur',
          })(<Input.Password size="large" />)}
        </Form.Item>
        <Form.Item label={t('login_security_account.confirm_password_label')}>
          {getFieldDecorator('confirmPassword', {
            rules: [
              {
                validator: (rule, value, callback) => {
                  if (!value || value.length === 0) {
                    callback(t('setPassword_form_not_empty'));
                  } else if (value.length < 8) {
                    callback(t('cms:cms.account.basic_setting.password.error.too_short'));
                  }

                  callback();
                },
              },
            ],
            validateTrigger: 'onBlur',
          })(<Input.Password size="large" />)}
        </Form.Item>
        <Form.Item>
          <Button
            size="large"
            type="primary"
            loading={loading}
            onClick={confirm}
            className={classes.submitBtn}
          >
            {t('landlord.select_property.confirm')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};
ResetPasswordForm.propTypes = {
  form: PropTypes.object.isRequired,
  email: PropTypes.string.isRequired,
  setShowSetPasswordForm: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};
const WrappedResetPasswordForm = Form.create({ name: 'resetPassword' })(ResetPasswordForm);

const LoginAndScurity = () => {
  const location = useLocation();
  const [showSetPasswordForm, setShowSetPasswordForm] = useState(false);
  const [currentMenuKey, setCurrentMenuKey] = useState(
    location.hash ? location.hash?.substr(1) : menuList.LOGIN_AND_SECURITY,
  );
  const { t } = useTranslation();
  const classes = uesStyles();
  const info = jwtDecode(cookies.get(cookieNames.token));
  const isLandlord = cookies.get(cookieNames.level)?.ACCOUNTLEVEL === 'LANDLORD';
  const { loading, data } = useQuery(QUERY_LANDLORD_LOGIN_SECURITY, {
    variables: { userUuid: info?.uuid },
  });
  const email = data?.landlordContact?.email;
  const phone = data?.landlordContact?.phone;
  const enabled = data?.landlordContact?.enabled;
  const id = data?.landlordContact?.id;

  const handleItemSelect = data => {
    setCurrentMenuKey(data.key);
  };

  useEffect(() => {
    event('Viewed Login& Security Page');
  }, []);

  return (
    <div className={classes.root}>
      <Menu
        className={classes.menu}
        defaultSelectedKeys={[currentMenuKey]}
        onSelect={handleItemSelect}
      >
        <Menu.Item key={menuList.LOGIN_AND_SECURITY}>{t('account_login_security_title')}</Menu.Item>

        <If condition={isLandlord}>
          <Menu.Item key={menuList.API_TOKEN}>
            {t('landlord.account.login_security.api_token.title')}
          </Menu.Item>
        </If>
      </Menu>
      {loading && <Spin size="large" className={classes.loading} />}
      <Choose>
        <When condition={!loading && currentMenuKey === menuList.LOGIN_AND_SECURITY}>
          <div className={classes.contentBox}>
            <div className={classes.titleWarp}>
              <h3 className={classes.title}>{t('account_login_security_title')}</h3>
            </div>
            <div>
              <div className={classes.fieldsBox}>
                <div className={classes.fieldCard}>
                  <h5 className={classes.fieldCardLabel}>{t('login_security_account.label')}</h5>
                  <span className={classes.fieldCardValue}>{email ? email : phone}</span>
                </div>

                {email && enabled && (
                  <div
                    className={classNames(classes.fieldCard, classes.fieldCardFlex, {
                      [classes.fieldBox]: showSetPasswordForm,
                    })}
                  >
                    <h5 className={classes.fieldCardLabel}>
                      {t('landlord.auth.main.password.label')}
                    </h5>

                    <div
                      className={classes.hintbtn}
                      onClick={() => {
                        setShowSetPasswordForm(!showSetPasswordForm);
                      }}
                    >
                      {!showSetPasswordForm
                        ? t('login_security_account.change_btn')
                        : t('login_security_account.cancel_btn')}
                    </div>
                  </div>
                )}
                {showSetPasswordForm && (
                  <WrappedResetPasswordForm
                    email={email}
                    id={id}
                    setShowSetPasswordForm={setShowSetPasswordForm}
                  />
                )}
              </div>
            </div>
          </div>
        </When>
        <When condition={isLandlord && !loading && currentMenuKey === menuList.API_TOKEN}>
          <APIToken t={t} />
        </When>
      </Choose>
    </div>
  );
};

export default LoginAndScurity;
